import React, { useState, useEffect, useContext } from 'react';
import { Modal, Button, Form, Table } from 'react-bootstrap';
import { showToast } from './CommonFunction';
import AsyncSelect from 'react-select/async';
import AuthContext from '../../context/AuthContext';
import axiosInstance from '../../utils/axios';
import BusyLoaderOverlay from './BusyLoaderOverlay';


const ModalMedicine = ({ show, handleClose, onSave, existingPrescriptions = '' }) => {
    const { userDetails } = useContext(AuthContext);
    const [medicineRows, setMedicineRows] = useState([
        { medicine: null, dosage: '', morning: '', afternoon: '', evening: '', night: '', other: '', source: 'new' }
    ]);
    const [prescribeMedicineList, setPrescribeMedicineList] = useState([]);
    const [newMedicine, setNewMedicine] = useState('');
    const [loading, setLoading] = useState(false);

    const onPressTableRowAdd = () => {
        setMedicineRows([...medicineRows, {
            medicine: null,
            dosage: '',
            morning: '',
            afternoon: '',
            evening: '',
            night: '',
            other: '',
            source: 'new'
        }]);
    }

    const loadMedicineOptions = async (inputValue) => {
        if (inputValue.length < 4) return [];
        setLoading(true);

        try {
            const response = await axiosInstance.get('/medicines/', {
                params: {
                    medicine_name: inputValue,
                    medicine_type: userDetails.clinic_type,
                },
            });

            return response.data.data.map(med => ({
                label: med.medicine_name,
                value: med.medicine_id,
            }));
        } catch (error) {
            showToast("error", error.response?.data?.message || "Failed to fetch medicines");
            return [];
        } finally {
            setLoading(false);
        }
    };

    const handleRowChange = (index, field, value) => {
        const updatedRows = [...medicineRows];
        updatedRows[index][field] = value;
        setMedicineRows(updatedRows);
    }

    const handleSelectMedicine = (index) => {
        const selectedRow = medicineRows[index];
        if (!selectedRow.medicine) return;

        const { medicine, dosage, morning, afternoon, evening, night, other } = selectedRow;

        let lines = [`${medicine}${dosage ? ` | Dosage: ${dosage}` : ''}`];
        if (morning) lines.push(`• Morning: ${morning}`);
        if (afternoon) lines.push(`• Afternoon: ${afternoon}`);
        if (evening) lines.push(`• Evening: ${evening}`);
        if (night) lines.push(`• Night: ${night}`);
        if (other) lines.push(`• Instruction: ${other}`);

        const updatedList = [...prescribeMedicineList];
        updatedList[index] = lines.join('\n');

        setPrescribeMedicineList(updatedList);
    };

    const handleAddNewMedicine = async () => {
        if (!newMedicine.trim()) return;
        setLoading(true);
        try {
            const res = await axiosInstance.post('/medicines/', {
                medicine_name: newMedicine,
                medicine_type: userDetails.clinic_type
            });
            if (res.status === 201) {
                showToast('success', 'New medicine added successfully!');
                setNewMedicine('');
            }
        } catch (err) {
            showToast('error', 'Error adding medicine');
            console.error("Error adding medicine", err);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteRow = (index) => {
        const updatedRows = [...medicineRows];
        const updatedPrescriptions = [...prescribeMedicineList];

        updatedRows.splice(index, 1);
        updatedPrescriptions.splice(index, 1);

        setMedicineRows(updatedRows);
        setPrescribeMedicineList(updatedPrescriptions);
    };

    useEffect(() => {
        if (show) {
            if (existingPrescriptions) {
                const blocks = existingPrescriptions.trim().split('\n\n');
                const parsedRows = blocks.map(block => {
                    const lines = block.split('\n');
                    const medicineLine = lines[0] || '';
                    let medicine = medicineLine;
                    let dosage = '';

                    // Parse with unique separator
                    if (medicineLine.includes('| Dosage:')) {
                        const parts = medicineLine.split('| Dosage:');
                        medicine = parts[0].trim();
                        dosage = parts[1]?.trim() || '';
                    }

                    const row = {
                        medicine,
                        dosage,
                        morning: '',
                        afternoon: '',
                        evening: '',
                        night: '',
                        other: '',
                        source: 'existing'
                    };

                    lines.slice(1).forEach(line => {
                        if (line.includes('Morning:')) row.morning = line.split('Morning:')[1].trim();
                        if (line.includes('Afternoon:')) row.afternoon = line.split('Afternoon:')[1].trim();
                        if (line.includes('Evening:')) row.evening = line.split('Evening:')[1].trim();
                        if (line.includes('Night:')) row.night = line.split('Night:')[1].trim();
                        if (line.includes('Instruction:')) row.other = line.split('Instruction:')[1].trim();
                    });

                    return row;
                });

                setMedicineRows(parsedRows);
                setPrescribeMedicineList(blocks);
            } else {
                setMedicineRows([
                    { medicine: null, dosage: '', morning: '', afternoon: '', evening: '', night: '', other: '', source: 'new' }
                ]);
                setPrescribeMedicineList([]);
            }
        }
    }, [show, existingPrescriptions]);

    return (
        <>
            <Modal show={show} onHide={handleClose} size="lg" backdrop="static" keyboard={false} centered dialogClassName="wide-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Prescribe Medicines</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Button variant="primary" className='mb-3' style={{ float: "right" }} onClick={onPressTableRowAdd}>Add Medicine</Button>

                    <Table bordered hover responsive className="w-100">
                        <thead>
                            <tr>
                                <th>Medicine</th>
                                <th>Dosage</th>
                                <th>Morning</th>
                                <th>Afternoon</th>
                                <th>Evening</th>
                                <th>Night</th>
                                <th>Other</th>
                                <th>Action</th>
                                <th>Delete</th>
                            </tr>
                        </thead>
                        <tbody>
                            {medicineRows.map((row, index) => (
                                <tr key={index}>
                                    <td style={{ minWidth: "300px" }}>
                                        {row.source === 'new' ? (
                                            <AsyncSelect
                                                className="w-100"
                                                styles={{
                                                    container: (base) => ({ ...base, width: '100%' }),
                                                    menuPortal: base => ({ ...base, zIndex: 9999 }),
                                                    menu: base => ({ ...base, zIndex: 9999 }),
                                                }}
                                                cacheOptions
                                                defaultOptions
                                                loadOptions={loadMedicineOptions}
                                                onChange={(selectedOption) => handleRowChange(index, 'medicine', selectedOption?.label || '')}
                                                menuPortalTarget={document.body}
                                            />
                                        ) : (
                                            <Form.Control type="text" value={row.medicine} className='bg-light' readOnly />
                                        )}
                                    </td>
                                    <td><Form.Control type="text" value={row.dosage} onChange={(e) => handleRowChange(index, 'dosage', e.target.value)} /></td>
                                    <td><Form.Control type="text" value={row.morning} onChange={(e) => handleRowChange(index, 'morning', e.target.value)} /></td>
                                    <td><Form.Control type="text" value={row.afternoon} onChange={(e) => handleRowChange(index, 'afternoon', e.target.value)} /></td>
                                    <td><Form.Control type="text" value={row.evening} onChange={(e) => handleRowChange(index, 'evening', e.target.value)} /></td>
                                    <td><Form.Control type="text" value={row.night} onChange={(e) => handleRowChange(index, 'night', e.target.value)} /></td>
                                    <td><Form.Control type="text" value={row.other} onChange={(e) => handleRowChange(index, 'other', e.target.value)} /></td>
                                    <td><Button variant="info" onClick={() => handleSelectMedicine(index)}>Select</Button></td>
                                    <td><Button variant="danger" onClick={() => handleDeleteRow(index)}>Delete</Button></td>
                                </tr>
                            ))}
                        </tbody>

                    </Table>

                    <Form.Group className="my-3">
                        <Form.Label>Presscribe Medicines</Form.Label>
                        <Form.Control as="textarea" rows={5} value={prescribeMedicineList.filter(Boolean).join('\n\n')} readOnly />
                    </Form.Group>

                    <Form.Group className="my-3">
                        <Form.Text className="text-muted">
                            Didn’t find the medicine you’re looking for? Add it below:
                        </Form.Text>
                        <div className="d-flex mt-2">
                            <Form.Control type="text" placeholder="Enter new medicine name" value={newMedicine} onChange={(e) => setNewMedicine(e.target.value)} />
                            <Button variant="primary" onClick={handleAddNewMedicine} className="ms-2" disabled={loading || userDetails.user_type === "M"}>Submit</Button>
                        </div>
                    </Form.Group>

                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => onSave(prescribeMedicineList.filter(Boolean).join('\n\n'))}>Save</Button>
                    <Button variant="secondary" onClick={handleClose}>Close</Button>
                </Modal.Footer>
            </Modal>
            <BusyLoaderOverlay show={loading} />
        </>
    );
}

export default ModalMedicine;